import React, { useState, useEffect, useCallback } from 'react';
import { ticketAPI } from '../services/api';
import Icon from './Icon';

// Status and priority badges
const StatusBadge = ({ status }) => {
  const statusStyles = {
    open: { bg: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', label: 'Open' },
    in_review: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'In Review' },
    waiting_response: { bg: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', label: 'Waiting Response' },
    resolved: { bg: 'rgba(26, 40, 17, 0.15)', color: '#4A8C18', label: 'Resolved' },
    closed: { bg: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', label: 'Closed' }
  };
  
  const style = statusStyles[status] || statusStyles.open;
  
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      background: style.bg,
      color: style.color
    }}>
      {style.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const priorityStyles = {
    urgent: { bg: 'rgba(239, 68, 68, 0.2)', color: '#f87171', label: 'Urgent' },
    high: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'High' },
    normal: { bg: 'rgba(59, 130, 246, 0.1)', color: '#60a5fa', label: 'Normal' },
    low: { bg: 'rgba(107, 114, 128, 0.1)', color: '#9ca3af', label: 'Low' }
  };
  
  const style = priorityStyles[priority] || priorityStyles.normal;
  
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      background: style.bg,
      color: style.color
    }}>
      {style.label}
    </span>
  );
};

const TypeBadge = ({ type, darkMode = true }) => {
  const typeConfig = {
    general: { label: 'General', icon: 'chat' },
    bug_report: { label: 'Bug', icon: 'bug_report' },
    feature_request: { label: 'Feature Request', icon: 'stars' },
    bike_block: { label: 'Blocked Bike', icon: 'block' },
    user_complaint: { label: 'User Complaint', icon: 'person' },
    company_complaint: { label: 'Company Complaint', icon: 'companies' },
    service_dispute: { label: 'Service Dispute', icon: 'service' },
    booking_issue: { label: 'Booking Issue', icon: 'calendar' },
    account_issue: { label: 'Account Issue', icon: 'lock' },
    billing: { label: 'Billing', icon: 'payment' },
    other: { label: 'Other', icon: 'description' }
  };
  
  const config = typeConfig[type] || { label: type, icon: 'help' };
  
  return (
    <span style={{
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '12px',
      background: 'var(--bg-overlay-medium)',
      color: 'rgba(255,255,255,0.7)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }}>
      <Icon name={config.icon} color="secondary" darkMode={darkMode} size={12} />
      {config.label}
    </span>
  );
};

function SuperAdminTickets({ companies = [], darkMode = true }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  
  // Selected ticket for details view
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Response form
  const [responseText, setResponseText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterType) params.ticket_type = filterType;
      if (filterCompany) params.company_id = filterCompany;
      if (showUnassignedOnly) params.unassigned = true;
      
      const response = await ticketAPI.getAllTickets(params);
      setTickets(response.data.tickets || []);
      setStats(response.data.stats || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterType, filterCompany, showUnassignedOnly]);

  const fetchSuperAdmins = async () => {
    try {
      await ticketAPI.getSuperAdmins();
    } catch (err) {
      console.error('Failed to fetch super admins:', err);
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchSuperAdmins();
  }, [fetchTickets]);

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetails(true);
    try {
      const response = await ticketAPI.getTicketAdmin(ticket.id);
      setTicketDetails(response.data.ticket);
    } catch (err) {
      setError('Failed to load ticket details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedTicket(null);
    setTicketDetails(null);
    setResponseText('');
    setIsInternal(false);
  };

  const handleAssignToMe = async (ticketId) => {
    try {
      await ticketAPI.assignTicket(ticketId);
      setSuccess('Ticket assigned to you');
      fetchTickets();
      if (ticketDetails?.id === ticketId) {
        handleViewTicket({ id: ticketId });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign ticket');
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await ticketAPI.updateTicket(ticketId, { status });
      setSuccess('Status updated');
      fetchTickets();
      if (ticketDetails?.id === ticketId) {
        handleViewTicket({ id: ticketId });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleUpdatePriority = async (ticketId, priority) => {
    try {
      await ticketAPI.updateTicket(ticketId, { priority });
      setSuccess('Priority updated');
      fetchTickets();
      if (ticketDetails?.id === ticketId) {
        handleViewTicket({ id: ticketId });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update priority');
    }
  };

  const handleResolve = async (ticketId, resolutionNotes = '') => {
    try {
      await ticketAPI.resolveTicket(ticketId, { resolution_notes: resolutionNotes });
      setSuccess('Ticket megoldva');
      fetchTickets();
      handleCloseDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resolve ticket');
    }
  };

  const handleClose = async (ticketId) => {
    try {
      await ticketAPI.closeTicket(ticketId);
      setSuccess('Ticket closed');
      fetchTickets();
      handleCloseDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close ticket');
    }
  };

  const handleAddResponse = async () => {
    if (!responseText.trim() || !ticketDetails) return;
    
    setSubmitting(true);
    try {
      await ticketAPI.addResponse(ticketDetails.id, {
        message: responseText,
        is_internal: isInternal
      });
      setSuccess('Reply sent');
      setResponseText('');
      setIsInternal(false);
      handleViewTicket({ id: ticketDetails.id });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add response');
    } finally {
      setSubmitting(false);
    }
  };

  // Stats cards
  const StatsCards = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '12px',
      marginBottom: '24px'
    }}>
      {[
        { label: 'Total', value: stats.total || 0, color: '#60a5fa' },
        { label: 'Open', value: stats.open || 0, color: '#60a5fa' },
        { label: 'In Review', value: stats.in_review || 0, color: '#fbbf24' },
        { label: 'Waiting Response', value: stats.waiting_response || 0, color: '#a78bfa' },
        { label: 'Urgent', value: stats.urgent || 0, color: '#f87171' },
        { label: 'Unassigned', value: stats.unassigned || 0, color: '#fb923c' }
      ].map(stat => (
        <div key={stat.label} style={{
          background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>{stat.label}</div>
        </div>
      ))}
    </div>
  );

  // Filters bar
  const FiltersBar = () => (
    <div style={{
      display: 'flex',
      gap: '12px',
      marginBottom: '20px',
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="form-input"
        style={{ width: 'auto', minWidth: '150px' }}
      >
        <option value="">All Statuses</option>
        <option value="open">Open</option>
        <option value="in_review">In Review</option>
        <option value="waiting_response">Waiting Response</option>
        <option value="resolved">Resolved</option>
        <option value="closed">Closed</option>
      </select>
      
      <select
        value={filterPriority}
        onChange={(e) => setFilterPriority(e.target.value)}
        className="form-input"
        style={{ width: 'auto', minWidth: '150px' }}
      >
        <option value="">All Priorities</option>
        <option value="urgent">Urgent</option>
        <option value="high">High</option>
        <option value="normal">Normal</option>
        <option value="low">Low</option>
      </select>
      
      <select
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        className="form-input"
        style={{ width: 'auto', minWidth: '150px' }}
      >
        <option value="">All Types</option>
        <option value="general">General</option>
        <option value="bug_report">Bug Report</option>
        <option value="bike_block">Blocked Bike</option>
        <option value="user_complaint">User Complaint</option>
        <option value="company_complaint">Company Complaint</option>
        <option value="service_dispute">Service Dispute</option>
        <option value="booking_issue">Booking Issue</option>
        <option value="billing">Billing</option>
      </select>
      
      {companies.length > 0 && (
        <select
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          className="form-input"
          style={{ width: 'auto', minWidth: '150px' }}
        >
          <option value="">All Companies</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      )}
      
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
        fontSize: '14px'
      }}>
        <input
          type="checkbox"
          checked={showUnassignedOnly}
          onChange={(e) => setShowUnassignedOnly(e.target.checked)}
        />
        Unassigned only
      </label>
      
      <button
        onClick={fetchTickets}
        className="btn btn-secondary"
        style={{ marginLeft: 'auto' }}
      >
        ↻ Refresh
      </button>
    </div>
  );

  // Ticket list
  const TicketList = () => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
      }}>
        <thead>
          <tr style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
            {['#', 'Type', 'Subject', 'Creator', 'Company', 'Priority', 'Status', 'Assigned', 'Created', 'Actions'].map(h => (
              <th key={h} style={{
                padding: '12px 8px',
                textAlign: 'left',
                color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                fontWeight: '500',
                fontSize: '12px',
                textTransform: 'uppercase'
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tickets.map(ticket => (
            <tr 
              key={ticket.id} 
              style={{ 
                borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                cursor: 'pointer',
                background: selectedTicket?.id === ticket.id 
                  ? (darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.2)')
                  : 'transparent'
              }}
              onClick={() => handleViewTicket(ticket)}
            >
              <td style={{ padding: '12px 8px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                #{ticket.id}
              </td>
              <td style={{ padding: '12px 8px' }}>
                <TypeBadge type={ticket.ticket_type} darkMode={darkMode} />
              </td>
              <td style={{ 
                padding: '12px 8px', 
                color: darkMode ? 'white' : '#1f2937', 
                fontWeight: '500',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {ticket.subject}
              </td>
              <td style={{ padding: '12px 8px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
                <div>{ticket.created_by_name || ticket.created_by_username}</div>
                <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                  {ticket.created_by_role}
                </div>
              </td>
              <td style={{ padding: '12px 8px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
                {ticket.company_name || '-'}
              </td>
              <td style={{ padding: '12px 8px' }}>
                <PriorityBadge priority={ticket.priority} />
              </td>
              <td style={{ padding: '12px 8px' }}>
                <StatusBadge status={ticket.status} />
              </td>
              <td style={{ padding: '12px 8px', color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563' }}>
                {ticket.assigned_to_name || ticket.assigned_to_username || (
                  <span style={{ color: '#f87171' }}>Nincs</span>
                )}
              </td>
              <td style={{ padding: '12px 8px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af', fontSize: '13px' }}>
                {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-GB') : '-'}
              </td>
              <td style={{ padding: '12px 8px' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {!ticket.assigned_to_user_id && ticket.status !== 'closed' && (
                    <button
                      onClick={() => handleAssignToMe(ticket.id)}
                      style={{
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#4A8C18'
                      }}
                    >
                      Take Over
                    </button>
                  )}
                  {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                    <button
                      onClick={() => handleResolve(ticket.id)}
                      style={{
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: '#60a5fa'
                      }}
                    >
                      Megoldva
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      {tickets.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎫</div>
          <p>No tickets matching the filter</p>
        </div>
      )}
    </div>
  );

  // Ticket details modal
  const TicketDetailsModal = () => {
    if (!selectedTicket) return null;
    
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '24px'
        }}
        onClick={handleCloseDetails}
      >
        <div
          style={{
            background: darkMode ? '#1a1a3e' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            borderRadius: '20px',
            padding: '28px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {loadingDetails ? (
            <div className="loading">Loading...</div>
          ) : ticketDetails ? (
            <>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', fontSize: '14px' }}>
                      Ticket #{ticketDetails.id}
                    </span>
                    <TypeBadge type={ticketDetails.ticket_type} darkMode={darkMode} />
                    <PriorityBadge priority={ticketDetails.priority} />
                    <StatusBadge status={ticketDetails.status} />
                  </div>
                  <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? 'white' : '#1f2937' }}>
                    {ticketDetails.subject}
                  </h2>
                </div>
                <button
                  onClick={handleCloseDetails}
                  style={{
                    background: 'var(--bg-overlay-medium)',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Info grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                borderRadius: '12px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '4px' }}>Creator</div>
                  <div style={{ color: darkMode ? 'white' : '#1f2937', fontWeight: '500' }}>
                    {ticketDetails.created_by_name || ticketDetails.created_by_username}
                  </div>
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                    {ticketDetails.created_by_role}
                  </div>
                </div>
                {ticketDetails.company_name && (
                  <div>
                    <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '4px' }}>Company</div>
                    <div style={{ color: darkMode ? 'white' : '#1f2937' }}>{ticketDetails.company_name}</div>
                  </div>
                )}
                {ticketDetails.bike_vin && (
                  <div>
                    <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '4px' }}>Motor</div>
                    <div style={{ color: darkMode ? 'white' : '#1f2937' }}>{ticketDetails.bike_info}</div>
                    <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                      Frame: {ticketDetails.bike_vin}
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '4px' }}>Assigned</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: darkMode ? 'white' : '#1f2937' }}>
                      {ticketDetails.assigned_to_name || ticketDetails.assigned_to_username || 'Nincs'}
                    </span>
                    {!ticketDetails.assigned_to_user_id && ticketDetails.status !== 'closed' && (
                      <button
                        onClick={() => handleAssignToMe(ticketDetails.id)}
                        style={{
                          padding: '2px 8px',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          background: 'var(--primary)',
                          color: '#0a0a1a'
                        }}
                      >
                        Take Over
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '4px' }}>Created</div>
                  <div style={{ color: darkMode ? 'white' : '#1f2937' }}>
                    {ticketDetails.created_at ? new Date(ticketDetails.created_at).toLocaleString('en-GB') : '-'}
                  </div>
                </div>
              </div>
              
              {/* Status/Priority controls */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <select
                  value={ticketDetails.status}
                  onChange={(e) => handleUpdateStatus(ticketDetails.id, e.target.value)}
                  className="form-input"
                  style={{ width: 'auto' }}
                >
                  <option value="open">Open</option>
                  <option value="in_review">In Review</option>
                  <option value="waiting_response">Waiting Response</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                
                <select
                  value={ticketDetails.priority}
                  onChange={(e) => handleUpdatePriority(ticketDetails.id, e.target.value)}
                  className="form-input"
                  style={{ width: 'auto' }}
                >
                  <option value="low">Low Priority</option>
                  <option value="normal">Normal Priority</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
                
                {ticketDetails.status !== 'resolved' && ticketDetails.status !== 'closed' && (
                  <button
                    onClick={() => handleResolve(ticketDetails.id)}
                    className="btn btn-primary"
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="success" color="primary" darkMode={darkMode} size={16} />
                      Resolved
                    </span>
                  </button>
                )}
                
                {ticketDetails.status !== 'closed' && (
                  <button
                    onClick={() => handleClose(ticketDetails.id)}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                )}
              </div>
              
              {/* Description */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: '14px' }}>
                  Description
                </h4>
                <div style={{
                  padding: '16px',
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '12px',
                  color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6'
                }}>
                  {ticketDetails.description}
                </div>
              </div>
              
              {/* Resolution notes */}
              {ticketDetails.resolution_notes && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#4A8C18', fontSize: '14px' }}>
                    ✓ Resolution
                  </h4>
                  <div style={{
                    padding: '16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {ticketDetails.resolution_notes}
                  </div>
                </div>
              )}
              
              {/* Responses */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: '14px' }}>
                  Communication ({ticketDetails.responses?.length || 0})
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(ticketDetails.responses || []).map(response => (
                    <div
                      key={response.id}
                      style={{
                        padding: '16px',
                        background: response.is_internal 
                          ? 'rgba(245, 158, 11, 0.1)' 
                          : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'),
                        borderRadius: '12px',
                        border: response.is_internal 
                          ? '1px solid rgba(245, 158, 11, 0.3)'
                          : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)')
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: darkMode ? 'white' : '#1f2937' }}>
                            {response.author_name || response.author_username}
                          </span>
                          <span style={{ 
                            fontSize: '11px', 
                            padding: '2px 6px', 
                            borderRadius: '4px',
                            background: response.author_role === 'super_admin' 
                              ? 'rgba(139, 92, 246, 0.2)' 
                              : 'rgba(59, 130, 246, 0.2)',
                            color: response.author_role === 'super_admin' ? '#a78bfa' : '#60a5fa'
                          }}>
                            {response.author_role}
                          </span>
                          {response.is_internal && (
                            <span style={{ 
                              fontSize: '11px', 
                              padding: '2px 6px', 
                              borderRadius: '4px',
                              background: 'rgba(245, 158, 11, 0.2)',
                              color: '#fbbf24'
                            }}>
                              🔒 Internal Note
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                          {response.created_at ? new Date(response.created_at).toLocaleString('en-GB') : ''}
                        </span>
                      </div>
                      <div style={{ 
                        color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6'
                      }}>
                        {response.message}
                      </div>
                    </div>
                  ))}
                  
                  {(!ticketDetails.responses || ticketDetails.responses.length === 0) && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px', 
                      color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af' 
                    }}>
                      No messages yet
                    </div>
                  )}
                </div>
              </div>
              
              {/* Add response form */}
              {ticketDetails.status !== 'closed' && (
                <div style={{
                  padding: '16px',
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: '14px' }}>
                    Send Reply
                  </h4>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Write your message..."
                    className="form-input"
                    style={{ minHeight: '100px', resize: 'vertical', marginBottom: '12px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
                      fontSize: '14px'
                    }}>
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Icon name="lock" color="warning" darkMode={darkMode} size={14} />
                        Internal Note (not visible to user)
                      </span>
                    </label>
                    <button
                      onClick={handleAddResponse}
                      disabled={!responseText.trim() || submitting}
                      className="btn btn-primary"
                    >
                      {submitting ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="error">Failed to load ticket details</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      borderRadius: '16px',
      padding: '24px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: darkMode ? 'white' : '#1f2937', fontSize: '18px' }}>
          🎫 Support Ticketek
        </h2>
      </div>
      
      {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success" style={{ marginBottom: '16px' }}>{success}</div>}
      
      <StatsCards />
      <FiltersBar />
      
      {loading ? (
        <div className="loading">Loading tickets...</div>
      ) : (
        <TicketList />
      )}
      
      <TicketDetailsModal />
    </div>
  );
}

export default SuperAdminTickets;

